<template>
  <div
    class="modal fade"
    id="package-companies-modal"
    tabindex="-1"
    role="dialog"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable pkg-modal-dialog" role="document">
      <div class="modal-content pkg-modal-content">

        <!-- Header -->
        <div class="pkg-header">
          <div class="pkg-header-inner">
            <div class="pkg-header-icon">
              <app-icon name="users" style="width:22px;height:22px;color:#fff;" />
            </div>
            <div class="pkg-header-text">
              <p class="pkg-label">Package Management</p>
              <h5 class="pkg-title">Companies Linked To Package</h5>
              <div class="pkg-subtitle">
                <span class="pkg-dot"></span>
                <span>{{ packageTitle }}</span>
              </div>
            </div>
          </div>
          <button type="button" class="pkg-close-btn" @click="closeModal" aria-label="Close">
            &times;
          </button>
        </div>

        <!-- Stats Bar -->
        <div class="pkg-stats-bar">
          <div class="d-flex align-items-center" style="gap:10px;">
            <div class="pkg-count-badge">{{ companies.length }}</div>
            <span class="pkg-stats-text">
              <strong>{{ companies.length === 1 ? '1 Company' : companies.length + ' Companies' }}</strong>
              <span class="text-muted"> assigned to this package</span>
            </span>
          </div>
          <div class="pkg-hint d-none d-md-flex">
            <app-icon name="refresh-cw" style="width:12px;height:12px;color:#9ca3af;margin-right:5px;" />
            <span>Select a package &amp; click ✓ to reassign</span>
          </div>
        </div>

        <!-- Body -->
        <div class="modal-body p-0 pkg-body">

          <!-- Loading -->
          <div v-if="loading" class="pkg-state-center">
            <div class="pkg-spinner-wrap">
              <div class="spinner-border text-primary" style="width:22px;height:22px;border-width:2.5px;" role="status">
                <span class="sr-only">Loading...</span>
              </div>
            </div>
            <p class="pkg-state-text">Loading companies...</p>
          </div>

          <!-- Empty -->
          <div v-else-if="!loading && companies.length === 0" class="pkg-state-center">
            <div class="pkg-empty-icon">
              <app-icon name="inbox" style="width:32px;height:32px;color:#93c5fd;" />
            </div>
            <h6 class="pkg-state-title">No Companies Found</h6>
            <p class="pkg-state-text">No companies are currently assigned to this package.</p>
          </div>

          <!-- Table -->
          <div v-else class="pkg-table-wrap">
            <table class="table mb-0 pkg-table">
              <thead>
                <tr class="pkg-thead-row">
                  <th class="pkg-th pkg-th-num">#</th>
                  <th class="pkg-th">Company</th>
                  <th class="pkg-th d-none d-md-table-cell">Email</th>
                  <th class="pkg-th d-none d-lg-table-cell">Phone</th>
                  <th class="pkg-th pkg-th-action">Switch Package</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(company, index) in companies"
                  :key="company.id"
                  class="pkg-tr"
                >
                  <td class="pkg-td pkg-td-num">{{ String(index + 1).padStart(2,'0') }}</td>
                  <td class="pkg-td">
                    <div class="pkg-company-cell">
                      <div class="pkg-avatar">
                        {{ (company.company_name || '?').charAt(0).toUpperCase() }}
                      </div>
                      <div>
                        <p class="pkg-company-name">{{ company.company_name || "—" }}</p>
                        <p class="pkg-company-email d-md-none">{{ company.company_email || "" }}</p>
                      </div>
                    </div>
                  </td>
                  <td class="pkg-td d-none d-md-table-cell pkg-muted">{{ company.company_email || "—" }}</td>
                  <td class="pkg-td d-none d-lg-table-cell pkg-muted">{{ company.company_phone || "—" }}</td>
                  <td class="pkg-td">
                    <div class="pkg-switch-cell">
                      <div class="pkg-select-wrap">
                        <select
                          v-model="company.selected_package_id"
                          :disabled="company.saving"
                          class="pkg-select"
                        >
                          <option v-for="pkg in availablePackages" :key="pkg.id" :value="pkg.id">
                            {{ pkg.title }}
                          </option>
                        </select>
                        <div class="pkg-select-arrow">
                          <svg width="10" height="10" viewBox="0 0 20 20" fill="currentColor">
                            <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd"/>
                          </svg>
                        </div>
                      </div>
                      <button
                        class="pkg-save-btn"
                        :class="{ 'pkg-save-btn--active': !company.saving && company.selected_package_id != packageId }"
                        :disabled="company.saving || company.selected_package_id == packageId"
                        @click="changePackage(company)"
                        title="Apply package change"
                      >
                        <span v-if="company.saving">
                          <span class="spinner-border" style="width:11px;height:11px;border-width:2px;"></span>
                        </span>
                        <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Footer -->
        <div class="pkg-footer">
          <button type="button" class="pkg-footer-btn" @click="closeModal">Close</button>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import CoreLibrary from "../../../../../core/helpers/CoreLibrary.js";
import * as actions from "../../../../Config/ApiUrl";
import { axiosPost } from "../../../../Helpers/AxiosHelper";

export default {
  name: "PackageCompaniesModal",
  extends: CoreLibrary,
  props: {
    packageId: { type: Number, required: true },
    packageTitle: { type: String, default: "" },
  },
  data() {
    return {
      loading: false,
      companies: [],
      availablePackages: [],
    };
  },
  mounted() {
    this.fetchCompanies();
    this.fetchPackages();
    this.$nextTick(() => {
      $("#package-companies-modal").modal("show");
      $("#package-companies-modal").on("hidden.bs.modal", () => {
        this.$emit("close-modal");
      });
    });
  },
  beforeDestroy() {
    $("#package-companies-modal").off("hidden.bs.modal");
  },
  methods: {
    fetchCompanies() {
      this.loading = true;
      this.axiosGet(`${actions.PACKAGE_COMPANIES}/${this.packageId}`)
        .then((res) => {
          this.companies = res.data.map((c) => ({
            ...c,
            selected_package_id: this.packageId,
            saving: false,
          }));
        })
        .catch(() => this.$toastr.e("Something went wrong."))
        .finally(() => { this.loading = false; });
    },
    fetchPackages() {
      this.axiosGet(actions.GET_PACKAGES)
        .then((res) => {
          this.availablePackages = res.data
            .filter((p) => p.id !== null)
            .map((p) => ({ id: p.id, title: p.value }));
        })
        .catch(() => {});
    },
    changePackage(company) {
      if (company.selected_package_id == this.packageId) return;
      this.$set(company, "saving", true);
      axiosPost(actions.PACKAGE_COMPANY_CHANGE, {
        company_id: company.id,
        package_id: company.selected_package_id,
      })
        .then(() => {
          this.$toastr.s(`Package updated for ${company.company_name}`);
          this.companies = this.companies.filter((c) => c.id !== company.id);
        })
        .catch(() => {
          this.$toastr.e("Failed to update. Please try again.");
          this.$set(company, "saving", false);
        });
    },
    closeModal() {
      $("#package-companies-modal").modal("hide");
      this.$emit("close-modal");
    },
  },
};
</script>

<style scoped>
/* Modal sizing — responsive */
.pkg-modal-dialog {
  width: 92%;
  max-width: 900px;
  margin: 1.5rem auto;
}
@media (max-width: 576px) {
  .pkg-modal-dialog { width: 98%; margin: .5rem auto; }
}

.pkg-modal-content {
  border: none;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.22);
  overflow: hidden;
}

/* Header */
.pkg-header {
  background: linear-gradient(135deg, #1e3a6e 0%, #2d5be3 100%);
  padding: 20px 28px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
}
.pkg-header-inner { display: flex; align-items: center; gap: 14px; }
.pkg-header-icon {
  width: 48px; height: 48px; border-radius: 12px; flex-shrink: 0;
  background: rgba(255,255,255,0.15); border: 1px solid rgba(255,255,255,0.2);
  display: flex; align-items: center; justify-content: center;
}
.pkg-label {
  margin: 0; color: rgba(255,255,255,0.6); font-size: 10px;
  font-weight: 700; letter-spacing: 1.2px; text-transform: uppercase;
}
.pkg-title {
  margin: 2px 0 4px; color: #fff; font-weight: 700; font-size: 17px; line-height: 1.2;
}
.pkg-subtitle { display: flex; align-items: center; gap: 6px; }
.pkg-dot { width: 6px; height: 6px; border-radius: 50%; background: #4ade80; display: inline-block; }
.pkg-subtitle span { color: rgba(255,255,255,0.8); font-size: 13px; }
.pkg-close-btn {
  width: 38px; height: 38px; border-radius: 10px; flex-shrink: 0;
  background: rgba(255,255,255,0.12); border: 1.5px solid rgba(255,255,255,0.25);
  color: #fff; font-size: 22px; line-height: 1;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: background .2s;
}
.pkg-close-btn:hover { background: rgba(255,255,255,0.25); }

/* Stats bar */
.pkg-stats-bar {
  background: #f7f9ff; border-bottom: 1px solid #e4eaf8;
  padding: 10px 28px; display: flex; align-items: center; justify-content: space-between;
  flex-wrap: wrap; gap: 8px;
}
.pkg-count-badge {
  width: 32px; height: 32px; border-radius: 9px; flex-shrink: 0;
  background: linear-gradient(135deg, #2d5be3, #1e3a6e);
  color: #fff; font-size: 13px; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
}
.pkg-stats-text { font-size: 13px; color: #374151; }
.pkg-hint { display: flex; align-items: center; color: #9ca3af; font-size: 12px; }

/* Body */
.pkg-body { max-height: 55vh; overflow-y: auto; overflow-x: hidden; }

/* States */
.pkg-state-center {
  display: flex; flex-direction: column; align-items: center;
  justify-content: center; padding: 48px 24px;
}
.pkg-spinner-wrap {
  width: 52px; height: 52px; border-radius: 14px; background: #eef2ff;
  display: flex; align-items: center; justify-content: center; margin-bottom: 14px;
}
.pkg-empty-icon {
  width: 68px; height: 68px; border-radius: 18px; background: #f0f4ff;
  display: flex; align-items: center; justify-content: center; margin-bottom: 14px;
}
.pkg-state-title { color: #374151; font-weight: 700; margin-bottom: 4px; }
.pkg-state-text { color: #9ca3af; font-size: 13px; margin: 0; text-align: center; }

/* Table */
.pkg-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
.pkg-table { font-size: 13px; border-collapse: collapse; min-width: 500px; width: 100%; }
.pkg-thead-row { background: #f0f4ff; }
.pkg-th {
  padding: 10px 14px; color: #6b7280; font-weight: 700; font-size: 10.5px;
  text-transform: uppercase; letter-spacing: .8px; border: none; white-space: nowrap;
}
.pkg-th-num { width: 46px; padding-left: 20px; }
.pkg-th-action { width: 230px; }

.pkg-tr { border-bottom: 1px solid #f0f4ff; transition: background .15s; }
.pkg-tr:hover { background: #fafbff; }
.pkg-td { padding: 10px 14px; vertical-align: middle; border: none; }
.pkg-td-num { color: #d1d5db; font-weight: 600; font-size: 12px; padding-left: 20px; }
.pkg-muted { color: #6b7280; }

/* Company cell */
.pkg-company-cell { display: flex; align-items: center; gap: 10px; }
.pkg-avatar {
  width: 34px; height: 34px; border-radius: 9px; flex-shrink: 0;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: #fff; font-size: 13px; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
}
.pkg-company-name { margin: 0; font-weight: 600; color: #111827; font-size: 13px; }
.pkg-company-email { margin: 2px 0 0; font-size: 11px; color: #9ca3af; }

/* Switch cell */
.pkg-switch-cell { display: flex; align-items: center; gap: 8px; }
.pkg-select-wrap { position: relative; flex: 1; min-width: 0; }
.pkg-select {
  width: 100%; height: 32px; border-radius: 7px;
  padding: 0 24px 0 10px; font-size: 12px; font-weight: 500;
  appearance: none; -webkit-appearance: none; cursor: pointer; outline: none;
  border: 2px solid #4f6ef7; background: #eef1ff; color: #1e3a6e;
  transition: border-color .2s, box-shadow .2s;
}
.pkg-select:focus { border-color: #2d5be3; box-shadow: 0 0 0 3px rgba(45,91,227,.15); }
.pkg-select:disabled { opacity: .6; cursor: not-allowed; }
.pkg-select-arrow {
  position: absolute; right: 7px; top: 50%; transform: translateY(-50%);
  pointer-events: none; color: #4f6ef7;
}
.pkg-save-btn {
  width: 32px; height: 32px; border-radius: 7px; border: none; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  background: #e5e7eb; color: #9ca3af; cursor: not-allowed; transition: all .2s;
}
.pkg-save-btn--active {
  background: linear-gradient(135deg, #2d5be3, #1e3a6e);
  color: #fff; cursor: pointer;
  box-shadow: 0 4px 12px rgba(45,91,227,.35);
}
.pkg-save-btn--active:hover { transform: scale(1.05); }

/* Footer */
.pkg-footer {
  background: #f7f9ff; border-top: 1px solid #e4eaf8;
  padding: 12px 28px; display: flex; justify-content: flex-end;
}
.pkg-footer-btn {
  height: 36px; padding: 0 22px; border-radius: 8px;
  border: 1.5px solid #e5e7eb; background: #fff;
  color: #374151; font-size: 13px; font-weight: 600; cursor: pointer; transition: background .2s;
}
.pkg-footer-btn:hover { background: #f9fafb; }
</style>
